/**
 * Список номенклатури. ч.1059 (рішення власника): картка позиції — окрема
 * сторінка /nomenclature/new і /nomenclature/:id (NomenclatureCardPage) з
 * чернеткою незбережених правок; тут лише список.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { nomenclature, type Nomenclature } from '@/api/catalogs';
import { refs } from '@/api/refs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';

export default function NomenclaturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');  // '' = всі види

  // TD-PAGINATION (2026-06-07): серверна пагінація; ч.691 — через
  // InfiniteDocGrid (шестерня колонок + ширини + канон).
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<Nomenclature>(
    (limit, offset, sort) => nomenclature.list({
      q: search || undefined, kind_id: kindFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, kindFilter],
  );
  // ч.691: колонка «Од.» — базова одиниця з класифікатора.
  const uomsList = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });
  // Види номенклатури (tenant-scoped) — колонка «Вид» і фільтр.
  const kindsList = useQuery({ queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list });
  const kindById = useMemo(
    () => new Map((kindsList.data ?? []).map((k) => [k.id, k])),
    [kindsList.data],
  );
  const uomById = useMemo(
    () => new Map((uomsList.data ?? []).map((u) => [u.id, u.short_name])),
    [uomsList.data],
  );

  const cols = useMemo<ColDef<Nomenclature>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      // Значок «І» імпорту з 1С.
      cellRenderer: (p: { data?: Nomenclature }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'code', headerName: t('nom.code'), width: 110, sortable: true },
    { field: 'article', headerName: t('nom.article'), width: 110, sortable: true },
    { field: 'name', headerName: t('nom.name'), flex: 1, minWidth: 280, sortable: true },
    {
      // Вид — valueGetter (резолв kind_id→name), серверна сортировка по UUID
      // немає сенсу. sortable:false.
      headerName: 'Вид',
      width: 140, sortable: false,
      valueGetter: (p) => (p.data?.kind_id ? kindById.get(p.data.kind_id)?.name ?? '' : ''),
    },
    {
      colId: '_uom', headerName: 'Од.',
      width: 80, sortable: false,
      valueGetter: (p) => (p.data?.base_uom_id ? uomById.get(p.data.base_uom_id) ?? '—' : '—'),
    },
    {
      field: 'is_service',
      headerName: 'Тип',
      width: 100, sortable: false,
      valueFormatter: (p) => (p.value ? 'Послуга' : 'Товар'),
    },
    { field: 'ukt_zed_code', headerName: t('nom.ukt_zed'), width: 130, sortable: true },
  ], [t, kindById, uomById]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {t('nom.title')}
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
              ({total})
            </span>
          )}
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <button className="primary" onClick={() => navigate('/nomenclature/new')}>+ {t('nom.new')}</button>
        </div>
      </div>
      {/* ч.691: фільтри — окремим рядком під назвою сторінки (канон). */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0 0.75rem' }}>
        <input
          type="search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Фільтр по виду номенклатури (2026-06-06). */}
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
          <option value="">Усі види</option>
          {(kindsList.data ?? []).map((k) => (
            <option key={k.id} value={k.id}>{k.name}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<Nomenclature>
        datasource={datasource}
        columnDefs={cols}
        storageKey="nomenclature"
        refreshRef={refreshRef}
        onRowDoubleClicked={(r) => navigate(`/nomenclature/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
